<template>
  <div class="electronic-map-page">
    <el-card class="tool-card" shadow="never" :style="{ width: leftPanelWidth + 'px' }">
      <el-tabs v-model="toolActiveTab" type="card" class="tool-tabs">
        <el-tab-pane name="recommend">
          <span slot="label">
            <i class="el-icon-place"></i>
            学生位置推荐最近校区
          </span>
          <div class="tool-section recommend-section">
            <el-tabs v-model="recommendInputTab" class="recommend-inner-tabs">
              <el-tab-pane label="智能分析" name="ai">
                <div class="recommend-mode-pane">
                  <el-form label-width="100px" size="small">
                    <el-form-item label="学生姓名">
                      <utwl-student-select
                        v-model="selectedStudentId"
                        placeholder="请选择学生"
                        width="100%"
                        :page-size="20"
                        @change="handleStudentSelect"
                      />
                    </el-form-item>
                    <el-form-item label="位置描述" required>
                      <el-input
                        v-model.trim="studentLocationDesc"
                        type="textarea"
                        :rows="3"
                        resize="none"
                        maxlength="200"
                        show-word-limit
                        placeholder="例如：天津市南开区长江道55号 / 天津市南开区南开大悦城 / 天津水上公园"
                      />
                    </el-form-item>
                    <div v-if="smartAnalyzeResultLng && smartAnalyzeResultLat" class="smart-analyze-coord">
                      经度：{{ smartAnalyzeResultLng }}&nbsp;&nbsp;&nbsp;&nbsp;纬度：{{ smartAnalyzeResultLat }}
                    </div>
                    <el-form-item>
                      <el-button type="primary" size="small" :loading="locationDescQueryLoading" @click="recommendByLocationDesc">{{ smartAnalyzeButtonText }}</el-button>
                    </el-form-item>
                  </el-form>
                </div>
              </el-tab-pane>
              <el-tab-pane label="地图标点" name="mapPoint">
                <div class="recommend-mode-pane">
                  <el-form label-width="100px" size="small">
                    <el-form-item label="坐标体系">
                      <el-select v-model="studentCoordType" style="width: 100%;">
                        <el-option label="GCJ-02（常见于国内地图）" value="GCJ02" />
                        <el-option label="WGS84（OpenStreetMap）" value="WGS84" />
                      </el-select>
                    </el-form-item>
                    <el-form-item label="经度">
                      <el-input v-model.trim="studentLng" placeholder="例如：117.20098" />
                    </el-form-item>
                    <el-form-item label="纬度">
                      <el-input v-model.trim="studentLat" placeholder="例如：39.08416" />
                    </el-form-item>
                    <div class="student-map-tip">点击右侧地图可快速填充经纬度！</div>
                    <el-form-item>
                      <el-button type="primary" size="small" @click="recommendNearestCampuses">{{ recommendButtonText }}</el-button>
                    </el-form-item>
                  </el-form>
                </div>
              </el-tab-pane>
            </el-tabs>
            <div class="recommend-table-wrap">
              <el-table :data="recommendListSorted" size="mini" border stripe class="recommend-table" :default-sort="{ prop: 'distanceKm', order: 'ascending' }" @row-click="handleRecommendRowClick">
                <el-table-column type="index" width="50" label="序号" />
                <el-table-column prop="orgName" label="机构" min-width="80" show-overflow-tooltip />
                <el-table-column prop="campusName" label="校区" min-width="100" show-overflow-tooltip>
                  <template slot-scope="{ row }">
                    <span v-if="recommendMinDistanceKm != null && row.distanceKm != null && !Number.isNaN(Number(row.distanceKm)) && Number(row.distanceKm) === recommendMinDistanceKm" class="recommend-min-tag recommend-badge-blink">[荐]</span> {{ row.campusName || '-' }}
                  </template>
                </el-table-column>
                <el-table-column prop="distanceKm" label="驾车距离" width="110" sortable>
                  <template slot-scope="{ row }">
                    <span v-if="row.distanceKm != null && !Number.isNaN(Number(row.distanceKm))">
                      {{ Number(row.distanceKm).toFixed(2) }}KM
                      <span v-if="recommendMinDistanceKm != null && Number(row.distanceKm) === recommendMinDistanceKm" class="recommend-min-tag">（近）</span>
                    </span>
                    <span v-else>-</span>
                  </template>
                </el-table-column>
                <el-table-column prop="durationSeconds" label="预计耗时" width="115" sortable>
                  <template slot-scope="{ row }">
                    <span class="recommend-duration-cell">
                      <span v-if="row.durationSeconds != null && !Number.isNaN(Number(row.durationSeconds))">
                        {{ formatDurationSeconds(row.durationSeconds) }}
                        <span v-if="recommendMinDurationSeconds != null && Number(row.durationSeconds) === recommendMinDurationSeconds" class="recommend-min-tag">（少）</span>
                      </span>
                      <span v-else>-</span>
                    </span>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </el-tab-pane>
        <el-tab-pane v-if="!mapMarkingTabRestricted" name="marking">
          <span slot="label">
            <i class="el-icon-add-location"></i>
            校区标注
          </span>
          <div class="tool-section marking-section">
            <el-form :model="markingForm" :rules="markingRules" ref="markingFormRef" label-width="100px" size="small">
              <el-form-item label="机构名" prop="orgId" required>
                <el-select v-model="markingForm.orgId" placeholder="请选择机构名" style="width: 100%;" @change="handleOrgChange" filterable clearable>
                  <el-option v-for="org in orgOptions" :key="org.value" :label="org.label" :value="org.value" />
                </el-select>
              </el-form-item>
              <el-form-item label="城市名" prop="cityLocationCode" required>
                <region-cascader
                  ref="markingRegionCascader"
                  v-model="markingForm.cityLocationCode"
                  placeholder="请选择省市区"
                  width="100%"
                  :check-strictly="true"
                  @change="handleCityLocationChange"
                />
              </el-form-item>
              <el-form-item label="校区名" prop="campusValue" required>
                <el-select
                  v-model="markingForm.campusValue"
                  placeholder="请选择校区名"
                  style="width: 100%;"
                  filterable
                  clearable
                  :loading="markingGeocodeLoading"
                  @change="handleCampusIndexChange"
                >
                  <el-option
                    v-for="campus in campusOptions"
                    :key="buildCampusValue(campus)"
                    :label="campus.campusName || ('校区#' + (campus.campusIndex + 1))"
                    :value="buildCampusValue(campus)"
                  />
                </el-select>
              </el-form-item>
              <div class="marking-address-tip">
                1、选择校区；2、右侧地图确认定位；3、手动修正（可选）；4、点击“完成标注”。
              </div>
              <el-form-item>
                <el-button size="small" icon="el-icon-search" @click="handleMarkingQuery">查询</el-button>
                <el-button size="small" icon="el-icon-refresh" @click="handleMarkingResetQuery">重置</el-button>
                <el-button type="primary" size="small" icon="el-icon-s-order" :loading="saveCampusPointLoading" @click="saveCampusPoint">完成标注</el-button>
              </el-form-item>
            </el-form>
            <div class="marking-table-wrap">
              <div class="marking-table-tip">
                <el-tooltip content="选中目标行可在右侧地图上定位该校区位置！" placement="top">
                  <i class="el-icon-info marking-table-tip-mark"></i>
                </el-tooltip>
                <span class="marking-table-stat">校区数：{{ markingStats.total }}</span>
                <span class="marking-table-stat marking-table-stat-marked">已标记：{{ markingStats.marked }}</span>
                <span class="marking-table-stat marking-table-stat-unmarked">未标记：{{ markingStats.unmarked }}</span>
              </div>
              <el-table
                ref="markingTableRef"
                :data="markingTableData"
                size="mini"
                border
                stripe
                height="calc(100% - 66px)"
                :row-class-name="getMarkingTableRowClassName"
                @row-click="handleMarkingRowClick"
                @cell-mouse-enter="handleMarkingCellMouseEnter"
                @cell-mouse-leave="handleMarkingCellMouseLeave"
              >
                <el-table-column type="index" label="序号" width="50" :index="markingTableIndexMethod" />
                <el-table-column prop="orgName" label="机构名" min-width="90" show-overflow-tooltip />
                <el-table-column label="所属行政区（县）" min-width="170" show-overflow-tooltip>
                  <template slot-scope="{ row }">
                    {{ resolveCampusRegionDisplay(row) }}
                  </template>
                </el-table-column>
                <el-table-column prop="campusName" label="校区名" min-width="90" show-overflow-tooltip />
                <el-table-column label="是否启用" width="88" align="center" show-overflow-tooltip>
                  <template slot-scope="{ row }">
                    {{ row.statusCode === 'ENABLED' ? '是' : '否' }}
                  </template>
                </el-table-column>
              </el-table>
              <el-pagination
                class="marking-pagination"
                small
                background
                layout="total, sizes, prev, pager, next"
                :total="markingTableAllData.length"
                :current-page="markingTablePage"
                :page-size="markingTablePageSize"
                :page-sizes="markingTablePageSizes"
                @size-change="handleMarkingPageSizeChange"
                @current-change="handleMarkingPageChange"
              />
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <div
      class="resize-handle"
      @mousedown="startResize"
    >
      <div class="resize-handle-bar" />
    </div>

    <el-card class="map-card" shadow="never">
      <div ref="mapEl" class="map-canvas"></div>
      <div class="map-toolbar">
        <div class="map-toolbar-tools">
          <span class="map-campus-all-switch" title="展示全部校区">
            <el-switch
              v-model="showAllCampusMarkers"
              :disabled="!map"
              @change="handleShowAllCampusMarkersChange"
            />
          </span>
          <el-tooltip content="放大" placement="top">
            <el-button type="primary" size="mini" icon="el-icon-zoom-in" circle @click="mapZoomIn" :disabled="!map" />
          </el-tooltip>
          <el-tooltip content="缩小" placement="top">
            <el-button type="primary" size="mini" icon="el-icon-zoom-out" circle @click="mapZoomOut" :disabled="!map" />
          </el-tooltip>
          <el-tooltip content="定位到我的位置" placement="top">
            <el-button type="primary" size="mini" icon="el-icon-location" circle @click="mapLocate" :disabled="!map" />
          </el-tooltip>
          <el-tooltip content="回到全国" placement="top">
            <el-button type="primary" size="mini" icon="el-icon-s-home" circle @click="mapHome" :disabled="!map" />
          </el-tooltip>
          <el-tooltip :content="isMapFullscreen ? '退出全屏' : '全屏地图'" placement="top">
            <el-button type="primary" size="mini" :icon="isMapFullscreen ? 'el-icon-copy-document' : 'el-icon-full-screen'" circle @click="mapToggleFullscreen" :disabled="!map" />
          </el-tooltip>
        </div>
        <div class="map-toolbar-info">
          <span class="map-toolbar-scale">比例尺：{{ mapScaleText }}</span>
          <span class="map-toolbar-zoom">级别：{{ mapZoomLevel }}</span>
        </div>
      </div>
    </el-card>
    <div
      class="map-delete-pop-anchor"
      :style="{ left: `${deletePopconfirm.left}px`, top: `${deletePopconfirm.top}px` }"
    >
      <el-popconfirm
        ref="mapDeletePopconfirmRef"
        :key="deletePopconfirm.key"
        trigger="click"
        popper-class="map-delete-popconfirm-popper"
        title="确认删除该校区地图标记？"
        confirm-button-text="确认"
        cancel-button-text="取消"
        confirm-button-type="text"
        cancel-button-type="text"
        @confirm="confirmDeleteCampusMarker"
        @cancel="hideDeletePopconfirm"
      >
        <button ref="mapDeletePopRef" slot="reference" type="button" class="map-delete-pop-ref"></button>
      </el-popconfirm>
    </div>
  </div>
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import api from '@/services/api';
import enabledCampusMarkerIcon from './campus-enabled-marker.svg';
import disabledCampusMarkerIcon from './campus-disabled-marker.svg';
import premarkCampusMarkerIcon from './campus-premark-marker.svg';
import studentMarkerPersonImage from './student-marker-person-watermarked.png';
import { getCachedMapResource, MAP_RESOURCE_CACHE_KEYS } from '@/utils/mapResourceCache';
import { getElectronicMapSettings } from '@/utils/electronicMapSettings';
import { canShowElectronicMapCampusMarkingTab } from '@/utils/sessionAuth';
import RegionCascader from '@/components/RegionCascader.vue';
import UtwlStudentSelect from '@/components/utwl-student-select.vue';

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow
});

const PI = Math.PI;
const A = 6378245.0;
const EE = 0.00669342162296594323;
// 为避免切换底图后继续读取旧 OSM 缓存，这里升级一版本地瓦片缓存前缀
const LOCAL_TILE_CACHE_PREFIX = 'emap_tile_v3_';
const LOCAL_TILE_INDEX_KEY = 'emap_tile_index_v3';
const LOCAL_TILE_MAX_ENTRIES = 120;
const LOCAL_TILE_MAX_DATAURL_LEN = 120000;
// 天地图：tk 与 sk 须为控制台同一应用下的一对密钥；企业版 WMTS 无 sk 会报 301020「安全核验 sk 码错误或丢失」
// 在 manageWeb/.env 中配置 VUE_APP_TDT_TOKEN、VUE_APP_TDT_SK（与项目根 .env 的 TDT_SK 同值；勿提交真实密钥）
const TDT_TOKEN = process.env.VUE_APP_TDT_TOKEN || '';
const TDT_SK = process.env.VUE_APP_TDT_SK || '';
const TDT_TILE_QUERY = () => {
  let q = 'tk=' + TDT_TOKEN;
  if (TDT_SK) q += '&sk=' + TDT_SK;
  return q;
};

function outOfChina(lng, lat) {
  return lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271;
}

function transformLat(x, y) {
  let ret = -100.0 + 2.0 * x + 3.0 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x));
  ret += (20.0 * Math.sin(6.0 * x * PI) + 20.0 * Math.sin(2.0 * x * PI)) * 2.0 / 3.0;
  ret += (20.0 * Math.sin(y * PI) + 40.0 * Math.sin(y / 3.0 * PI)) * 2.0 / 3.0;
  ret += (160.0 * Math.sin(y / 12.0 * PI) + 320 * Math.sin(y * PI / 30.0)) * 2.0 / 3.0;
  return ret;
}

function transformLng(x, y) {
  let ret = 300.0 + x + 2.0 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x));
  ret += (20.0 * Math.sin(6.0 * x * PI) + 20.0 * Math.sin(2.0 * x * PI)) * 2.0 / 3.0;
  ret += (20.0 * Math.sin(x * PI) + 40.0 * Math.sin(x / 3.0 * PI)) * 2.0 / 3.0;
  ret += (150.0 * Math.sin(x / 12.0 * PI) + 300.0 * Math.sin(x / 30.0 * PI)) * 2.0 / 3.0;
  return ret;
}

function wgs84ToGcj02(lng, lat) {
  if (outOfChina(lng, lat)) return [lng, lat];
  let dLat = transformLat(lng - 105.0, lat - 35.0);
  let dLng = transformLng(lng - 105.0, lat - 35.0);
  const radLat = lat / 180.0 * PI;
  let magic = Math.sin(radLat);
  magic = 1 - EE * magic * magic;
  const sqrtMagic = Math.sqrt(magic);
  dLat = (dLat * 180.0) / ((A * (1 - EE)) / (magic * sqrtMagic) * PI);
  dLng = (dLng * 180.0) / (A / sqrtMagic * Math.cos(radLat) * PI);
  return [lng + dLng, lat + dLat];
}

function gcj02ToWgs84(lng, lat) {
  if (outOfChina(lng, lat)) return [lng, lat];
  const gcj = wgs84ToGcj02(lng, lat);
  return [lng * 2 - gcj[0], lat * 2 - gcj[1]];
}

function haversineKm(lng1, lat1, lng2, lat2) {
  const toRad = d => d * PI / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/** 计算路径总长度（米），points 为 [[lat,lng],...] */
function pathDistanceMeters(points) {
  if (!points || points.length < 2) return 0;
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const [lat1, lng1] = points[i];
    const [lat2, lng2] = points[i + 1];
    total += haversineKm(lng1, lat1, lng2, lat2) * 1000;
  }
  return total;
}

/**
 * 车辆轨迹跟踪动画（等效天地图 CarTrack）
 * @param {L.Map} map - Leaflet 地图实例
 * @param {Array} points - [[lat,lng],...] 路径点
 * @param {Object} opts - 支持用户配置：interval, speed, passedColor, notPassedColor, lineWeight, lineOpacity, carIconSize, carEmoji, tooltipText, showHistoryTrack, showTooltip, loop
 */
function createCarTrack(map, points, opts = {}) {
  const interval = Math.max(10, Math.min(500, Number(opts.interval) || 25));
  const speed = Math.max(0, Number(opts.speed) || 500);
  const passedColor = opts.passedColor || '#0000ff';
  const notPassedColor = opts.notPassedColor || '#ff0000';
  const lineWeight = Math.max(1, Math.min(20, Number(opts.lineWeight) || 6));
  const lineOpacity = Math.max(0, Math.min(1, Number(opts.lineOpacity) ?? 0.9));
  const tooltipText = opts.tooltipText || '';
  const showHistoryTrack = opts.showHistoryTrack !== false;
  const showTooltip = opts.showTooltip !== false;
  const carIconSize = Math.max(16, Math.min(64, Number(opts.carIconSize) || 28));
  const carEmoji = (opts.carEmoji && String(opts.carEmoji).trim()) || '🚗';

  const totalDist = pathDistanceMeters(points);
  if (totalDist <= 0) return null;

  const passedLine = L.polyline([], {
    color: passedColor,
    weight: lineWeight,
    opacity: lineOpacity
  });
  if (showHistoryTrack) passedLine.addTo(map);
  const notPassedLine = L.polyline(points, {
    color: notPassedColor,
    weight: lineWeight,
    opacity: lineOpacity
  }).addTo(map);
  if (showTooltip && tooltipText) {
    notPassedLine.bindTooltip(tooltipText, {
      permanent: true,
      direction: 'center',
      className: 'recommend-line-tooltip'
    });
  }

  const half = Math.floor(carIconSize / 2);
  const carIcon = L.divIcon({
    className: 'car-track-marker-wrap',
    html: `<div class="car-track-marker" style="font-size:${Math.max(14, carIconSize - 4)}px">${carEmoji}</div>`,
    iconSize: [carIconSize, carIconSize],
    iconAnchor: [half, half]
  });
  const carMarker = L.marker(points[0], { icon: carIcon, zIndexOffset: 100 }).addTo(map);

  let animStartTime = 0;
  let timerId = null;
  const loop = opts.loop !== false;

  function getPositionAtProgress(p) {
    if (p <= 0) return { lat: points[0][0], lng: points[0][1], segmentIndex: 0, t: 0 };
    if (p >= 1) return { lat: points[points.length - 1][0], lng: points[points.length - 1][1], segmentIndex: points.length - 2, t: 1 };
    let dist = p * totalDist;
    for (let i = 0; i < points.length - 1; i++) {
      const [lat1, lng1] = points[i];
      const [lat2, lng2] = points[i + 1];
      const segLen = haversineKm(lng1, lat1, lng2, lat2) * 1000;
      if (dist <= segLen) {
        const t = segLen > 0 ? dist / segLen : 0;
        return {
          lat: lat1 + (lat2 - lat1) * t,
          lng: lng1 + (lng2 - lng1) * t,
          segmentIndex: i,
          t
        };
      }
      dist -= segLen;
    }
    return { lat: points[points.length - 1][0], lng: points[points.length - 1][1], segmentIndex: points.length - 2, t: 1 };
  }

  function getPassedPoints(progress) {
    if (progress <= 0) return [];
    const pos = getPositionAtProgress(progress);
    const idx = pos.segmentIndex;
    const passed = points.slice(0, idx + 1);
    if (pos.t > 0.001) {
      passed.push([pos.lat, pos.lng]);
    }
    return passed;
  }

  function getNotPassedPoints(progress) {
    if (progress >= 1) return [];
    const pos = getPositionAtProgress(progress);
    const idx = pos.segmentIndex;
    const notPassed = [[pos.lat, pos.lng]];
    for (let i = idx + 1; i < points.length; i++) {
      notPassed.push(points[i]);
    }
    return notPassed;
  }

  const cycleTimeSec = totalDist / speed;

  function tick() {
    const elapsed = (Date.now() - animStartTime) / 1000;
    let progress = loop ? (elapsed % cycleTimeSec) / cycleTimeSec : Math.min(1, (elapsed * speed) / totalDist);
    if (!loop && progress >= 1) {
      progress = 1;
      stop();
    }
    const pos = getPositionAtProgress(progress);
    carMarker.setLatLng([pos.lat, pos.lng]);
    passedLine.setLatLngs(getPassedPoints(progress));
    notPassedLine.setLatLngs(getNotPassedPoints(progress));
  }

  function start() {
    stop();
    animStartTime = Date.now();
    timerId = setInterval(tick, interval);
    tick();
  }

  function stop() {
    if (timerId) {
      clearInterval(timerId);
      timerId = null;
    }
  }

  function destroy() {
    stop();
    if (map.hasLayer(passedLine)) map.removeLayer(passedLine);
    map.removeLayer(notPassedLine);
    map.removeLayer(carMarker);
  }

  return { start, stop, destroy, passedLine, notPassedLine, carMarker };
}

function getLocalStorageSafe() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
  } catch (e) {
    // ignore
  }
  return null;
}

function readTileIndex() {
  const storage = getLocalStorageSafe();
  if (!storage) return {};
  try {
    const raw = storage.getItem(LOCAL_TILE_INDEX_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
}

function writeTileIndex(indexMap) {
  const storage = getLocalStorageSafe();
  if (!storage) return;
  try {
    storage.setItem(LOCAL_TILE_INDEX_KEY, JSON.stringify(indexMap || {}));
  } catch (e) {
    // ignore
  }
}

function touchTileIndexKey(tileKey) {
  const idx = readTileIndex();
  idx[tileKey] = Date.now();
  writeTileIndex(idx);
}

function compactTileCache(indexMap, targetSize = LOCAL_TILE_MAX_ENTRIES) {
  const storage = getLocalStorageSafe();
  if (!storage) return {};
  const keys = Object.keys(indexMap || {});
  if (keys.length <= targetSize) return indexMap || {};
  const sorted = keys.sort((a, b) => (indexMap[a] || 0) - (indexMap[b] || 0));
  const removeCount = keys.length - targetSize;
  for (let i = 0; i < removeCount; i++) {
    const key = sorted[i];
    storage.removeItem(`${LOCAL_TILE_CACHE_PREFIX}${key}`);
    delete indexMap[key];
  }
  return indexMap;
}

function getTileDataFromLocal(tileKey) {
  const storage = getLocalStorageSafe();
  if (!storage) return null;
  try {
    const data = storage.getItem(`${LOCAL_TILE_CACHE_PREFIX}${tileKey}`);
    if (!data) return null;
    touchTileIndexKey(tileKey);
    return data;
  } catch (e) {
    return null;
  }
}

function saveTileDataToLocal(tileKey, dataUrl) {
  if (!dataUrl || dataUrl.length > LOCAL_TILE_MAX_DATAURL_LEN) return;
  const storage = getLocalStorageSafe();
  if (!storage) return;
  const key = `${LOCAL_TILE_CACHE_PREFIX}${tileKey}`;
  let indexMap = readTileIndex();
  try {
    storage.setItem(key, dataUrl);
    indexMap[tileKey] = Date.now();
    indexMap = compactTileCache(indexMap, LOCAL_TILE_MAX_ENTRIES);
    writeTileIndex(indexMap);
  } catch (e) {
    try {
      indexMap = compactTileCache(indexMap, Math.floor(LOCAL_TILE_MAX_ENTRIES * 0.6));
      storage.setItem(key, dataUrl);
      indexMap[tileKey] = Date.now();
      writeTileIndex(indexMap);
    } catch (err) {
      // ignore quota failures
    }
  }
}

function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function createLocalCachedTileLayer(urlTemplate, options) {
  const LocalCachedLayer = L.TileLayer.extend({
    createTile(coords, done) {
      const tile = document.createElement('img');
      L.DomEvent.on(tile, 'load', L.bind(this._tileOnLoad, this, done, tile));
      L.DomEvent.on(tile, 'error', L.bind(this._tileOnError, this, done, tile));
      if (this.options.crossOrigin || this.options.crossOrigin === '') {
        tile.crossOrigin = this.options.crossOrigin === true ? '' : this.options.crossOrigin;
      }
      tile.alt = '';
      tile.setAttribute('role', 'presentation');

      const tileKey = `${coords.z}/${coords.x}/${coords.y}`;
      const cached = getTileDataFromLocal(tileKey);
      if (cached) {
        tile.src = cached;
        return tile;
      }

      const tileUrl = this.getTileUrl(coords);
      fetch(tileUrl, { cache: 'force-cache' })
        .then(resp => {
          if (!resp.ok) throw new Error('tile fetch failed');
          return resp.blob();
        })
        .then(blobToDataURL)
        .then(dataUrl => {
          saveTileDataToLocal(tileKey, dataUrl);
          tile.src = dataUrl;
        })
        .catch(() => {
          tile.src = tileUrl;
        });
      return tile;
    }
  });
  return new LocalCachedLayer(urlTemplate, options);
}

export default {
  name: 'ElectronicMap',
  components: {
    RegionCascader,
    UtwlStudentSelect
  },
  data() {
    return {
      chinaBounds: [
        [0.8293, 72.004],
        [55.8271, 137.8347]
      ],
      map: null,
      mapScaleText: '-',
      mapZoomLevel: '-',
      isMapFullscreen: false,
      campusLayer: null,
      pendingMarker: null,
      studentMarker: null,
      recommendMarkers: [],
      recommendLines: [],
      recommendAnimatedLines: [],
      recommendCarTracks: [],
      recommendLineAnimTimer: null,
      recommendLineDashOffset: 0,
      campusPoints: [],
      showAllCampusMarkers: false,
      campusMarkerDebounceTimer: null,
      allCampusPointsLoaded: false,
      campusPointsProvinceCode: '',
      markingForm: {
        orgId: '',
        cityLocationCode: '',
        campusValue: ''
      },
      markingRules: {
        orgId: [{ required: true, message: '请选择机构', trigger: ['blur', 'change'] }],
        cityLocationCode: [{ required: true, message: '请选择城市名', trigger: ['blur', 'change'] }],
        campusValue: [{ required: true, message: '请选择校区', trigger: ['blur', 'change'] }]
      },
      pendingPoint: null,
      toolActiveTab: 'recommend',
      recommendInputTab: 'ai',
      selectedStudentId: '',
      selectedStudentOption: null,
      enabledCampusMarkerIconUrl: getCachedMapResource(MAP_RESOURCE_CACHE_KEYS.enabledCampusMarker, enabledCampusMarkerIcon),
      disabledCampusMarkerIconUrl: getCachedMapResource(MAP_RESOURCE_CACHE_KEYS.disabledCampusMarker, disabledCampusMarkerIcon),
      premarkCampusMarkerIconUrl: getCachedMapResource(MAP_RESOURCE_CACHE_KEYS.premarkCampusMarker, premarkCampusMarkerIcon),
      studentMarkerPersonImageUrl: getCachedMapResource(MAP_RESOURCE_CACHE_KEYS.studentMarker, studentMarkerPersonImage),
      studentCoordType: 'WGS84',
      studentLocationDesc: '',
      locationDescQueryLoading: false,
      smartAnalyzeResultLng: '',
      smartAnalyzeResultLat: '',
      studentLng: '',
      studentLat: '',
      recommendList: [],
      recommendCountdown: 0,
      recommendCountdownTimer: null,
      markingTableAllData: [],
      markingTableData: [],
      markingTablePage: 1,
      markingTablePageSize: 10,
      markingTablePageSizes: [10, 20, 50, 100],
      saveCampusPointLoading: false,
      markingGeocodeLoading: false,
      deleteCampusMarkerLoading: false,
      refreshingCampusData: false,
      deletePopconfirm: {
        left: 0,
        top: 0,
        payload: null,
        key: 0
      },
      markingOrgDefaultApplied: false,
      leftPanelWidth: 540,
      resizeStartX: 0,
      resizeStartWidth: 0
    };
  },
  computed: {
    smartAnalyzeButtonText() {
      return this.locationDescQueryLoading ? '分析中，请稍等' : '开始分析';
    },
    recommendButtonText() {
      return this.recommendCountdown > 0
        ? `推荐最近5个校区（${this.recommendCountdown}秒）`
        : '推荐最近5个校区';
    },
    pendingPointText() {
      if (!this.pendingPoint) return '请在地图上点击标点';
      return `${this.pendingPoint.lng.toFixed(6)}, ${this.pendingPoint.lat.toFixed(6)}（WGS84）`;
    },
    orgOptions() {
      const map = {};
      (this.campusPoints || []).forEach(item => {
        map[item.orgId] = item.orgName;
      });
      return Object.keys(map).map(orgId => ({ value: orgId, label: map[orgId] }));
    },
    campusOptions() {
      return (this.campusPoints || []).filter(item => {
        if (String(item.orgId) !== String(this.markingForm.orgId)) return false;
        if (!this.markingForm.cityLocationCode) return false;
        return this.isCampusInSelectedRegion(item, this.markingForm.cityLocationCode);
      });
    },
    recommendMinDistanceKm() {
      const valid = (this.recommendList || []).filter(r => r.distanceKm != null && !Number.isNaN(Number(r.distanceKm)));
      if (!valid.length) return null;
      return Math.min(...valid.map(r => Number(r.distanceKm)));
    },
    recommendMinDurationSeconds() {
      const valid = (this.recommendList || []).filter(r => r.durationSeconds != null && !Number.isNaN(Number(r.durationSeconds)));
      if (!valid.length) return null;
      return Math.min(...valid.map(r => Number(r.durationSeconds)));
    },
    recommendListSorted() {
      const list = this.recommendList || [];
      return [...list].sort((a, b) => {
        const da = a.distanceKm != null && !Number.isNaN(Number(a.distanceKm)) ? Number(a.distanceKm) : Infinity;
        const db = b.distanceKm != null && !Number.isNaN(Number(b.distanceKm)) ? Number(b.distanceKm) : Infinity;
        return da - db;
      });
    },
    markingStats() {
      const list = Array.isArray(this.markingTableAllData) ? this.markingTableAllData : [];
      const total = list.length;
      const marked = list.filter(item => this.isCampusMarked(item)).length;
      return {
        total,
        marked,
        unmarked: total - marked
      };
    },
    /**
     * 为 true 时隐藏「校区标注」及关联删除/保存：
     * 默认隐藏，仅 admin / SYSTEM_ADMIN / BUSINESS / OPERATION 可见（见 sessionAuth）。
     */
    mapMarkingTabRestricted() {
      return !canShowElectronicMapCampusMarkingTab();
    }
  },
  watch: {
    toolActiveTab(newVal, oldVal) {
      if (this.mapMarkingTabRestricted && newVal === 'marking') {
        this.$nextTick(() => {
          this.toolActiveTab = 'recommend';
        });
        return;
      }
      if (oldVal === 'marking' && newVal !== 'marking') {
        this.clearMarkingPendingPreview();
      }
      if (newVal === 'marking') {
        this.stopRecommendFlow();
        this.ensureMarkingTableScrollListener();
        this.loadCampusPoints({ forceAll: true }).then(() => {
          const defaultChanged = this.tryApplyInitialMarkingOrgDefault();
          if (defaultChanged) this.handleMarkingQuery();
        });
      }
    },
    recommendInputTab(newVal) {
      if (newVal !== 'mapPoint') {
        if (this.recommendCountdownTimer) {
          clearInterval(this.recommendCountdownTimer);
          this.recommendCountdownTimer = null;
        }
        this.recommendCountdown = 0;
      }
    }
  },
  async mounted() {
    if (this.mapMarkingTabRestricted) {
      this.toolActiveTab = 'recommend';
    }
    this.initMap();
    await this.refreshLatestMapData();
    this.ensureMarkingTableScrollListener();
    document.addEventListener('fullscreenchange', this.handleMapFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleMapFullscreenChange);
  },
  async activated() {
    if (this.mapMarkingTabRestricted) {
      this.toolActiveTab = 'recommend';
    }
    await this.refreshLatestMapData();
    this.ensureMarkingTableScrollListener();
  },
  beforeDestroy() {
    document.removeEventListener('fullscreenchange', this.handleMapFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.handleMapFullscreenChange);
    document.removeEventListener('mousemove', this.onResizeMove);
    document.removeEventListener('mouseup', this.onResizeEnd);
    if (this.recommendCountdownTimer) {
      clearInterval(this.recommendCountdownTimer);
      this.recommendCountdownTimer = null;
    }
    if (this.recommendLineAnimTimer) {
      clearInterval(this.recommendLineAnimTimer);
      this.recommendLineAnimTimer = null;
    }
    if (this.campusMarkerDebounceTimer) {
      clearTimeout(this.campusMarkerDebounceTimer);
      this.campusMarkerDebounceTimer = null;
    }
    if (this.map) {
      this.map.off('click');
      this.map.off('popupopen');
      this.map.off('popupclose');
      this.map.off('tooltipopen');
      this.map.off('tooltipclose');
      this.map.remove();
    }
  },
  methods: {
    startResize(e) {
      this.resizeStartX = e.clientX;
      this.resizeStartWidth = this.leftPanelWidth;
      document.addEventListener('mousemove', this.onResizeMove);
      document.addEventListener('mouseup', this.onResizeEnd);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    },
    onResizeMove(e) {
      const delta = e.clientX - this.resizeStartX;
      let w = this.resizeStartWidth + delta;
      w = Math.max(480, Math.min(900, w));
      this.leftPanelWidth = w;
    },
    onResizeEnd() {
      document.removeEventListener('mousemove', this.onResizeMove);
      document.removeEventListener('mouseup', this.onResizeEnd);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    },
    formatDurationSeconds(seconds) {
      if (seconds == null || Number.isNaN(Number(seconds)) || Number(seconds) < 0) return '-';
      const total = Math.floor(Number(seconds));
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      const parts = [];
      if (h > 0) parts.push(`${h}时`);
      if (m > 0) parts.push(`${m}分`);
      parts.push(`${s}秒`);
      return parts.join('');
    },
    async refreshLatestMapData() {
      if (this.refreshingCampusData) return;
      this.refreshingCampusData = true;
      try {
        await this.ensureCampusPointsForCurrentView({ force: true });
        this.handleMarkingQuery();
      } finally {
        this.refreshingCampusData = false;
      }
    },
    async handleStudentSelect(studentId, selectedStudent) {
      if (!studentId) {
        this.selectedStudentOption = null;
        this.refreshCampusMarkers();
        return;
      }
      const row = selectedStudent && String(selectedStudent.id) === String(studentId)
        ? selectedStudent
        : null;
      if (!row) return;
      this.selectedStudentOption = row;
      const regionParts = [row.provinceName, row.cityName, row.districtName]
        .map(v => (v || '').trim())
        .filter(Boolean)
        .filter((part, idx, arr) => arr.indexOf(part) === idx);
      const locationText = [...regionParts, (row.addressDetail || '').trim()]
        .filter(Boolean)
        .join(' ');
      if (locationText) {
        this.studentLocationDesc = locationText;
      }
      if (!this.showAllCampusMarkers && row.provinceCode) {
        await this.loadCampusPoints({ provinceCode: row.provinceCode || '', force: true });
      }
      this.refreshCampusMarkers();
    },
    initMap() {
      this.map = L.map(this.$refs.mapEl, {
        zoomControl: true,
        minZoom: 4,
        maxBounds: this.chinaBounds,
        maxBoundsViscosity: 1.0,
        worldCopyJump: false
      }).setView([35.8617, 104.1954], 4);

      // 当前主用底图：天地图二维矢量地图（vec_w）+ 注记（cva_w）
      // 使用最简单的 L.tileLayer 叠加两层，不做本地缓存
      const tdtVecUrl =
        'https://t{s}.tianditu.gov.cn/vec_w/wmts?' +
        'SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&' +
        'LAYER=vec&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&' +
        'TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&' + TDT_TILE_QUERY();
      const tdtCvaUrl =
        'https://t{s}.tianditu.gov.cn/cva_w/wmts?' +
        'SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&' +
        'LAYER=cva&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&' +
        'TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&' + TDT_TILE_QUERY();

      L.tileLayer(tdtVecUrl, {
        subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
        minZoom: 4,
        maxZoom: 18,
        noWrap: true,
        bounds: this.chinaBounds,
        crossOrigin: true,
        attribution: '&copy; 天地图 &amp; OpenStreetMap'
      }).addTo(this.map);
      L.tileLayer(tdtCvaUrl, {
        subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
        minZoom: 4,
        maxZoom: 18,
        noWrap: true,
        bounds: this.chinaBounds,
        crossOrigin: true
      }).addTo(this.map);

      // 备用方案：仍保留基于后端转发的 OSM 瓦片（如需切回，只需改回下面这一行）
      // createLocalCachedTileLayer('/api/map/tile?z={z}&x={x}&y={y}', {
      //   minZoom: 4,
      //   maxZoom: 19,
      //   bounds: this.chinaBounds,
      //   noWrap: true,
      //   updateWhenIdle: true,
      //   keepBuffer: 1,
      //   attribution: '&copy; OpenStreetMap contributors'
      // }).addTo(this.map);
      this.campusLayer = L.layerGroup().addTo(this.map);
      this.map.on('click', this.handleMapClick);
      this.map.on('popupopen', this.handleMapPopupOpen);
      this.map.on('popupclose', this.handleMapPopupClose);
      this.map.on('tooltipopen', this.handleMapTooltipOpen);
      this.map.on('tooltipclose', this.handleMapTooltipClose);
      this.map.on('zoomend moveend', this.updateMapScaleAndZoom);
      L.control.scale({ imperial: false }).addTo(this.map);
      this.$nextTick(() => this.updateMapScaleAndZoom());
    },
    updateMapScaleAndZoom() {
      if (!this.map) return;
      const zoom = this.map.getZoom();
      this.mapZoomLevel = zoom != null ? String(zoom) : '-';
      const center = this.map.getCenter();
      if (center) {
        const lat = center.lat * (Math.PI / 180);
        const metersPerPixel = (40075016.686 * Math.abs(Math.cos(lat))) / Math.pow(2, zoom + 8);
        const scaleDenom = Math.round(metersPerPixel * 3780);
        if (scaleDenom >= 10000) {
          this.mapScaleText = `1:${(scaleDenom / 10000).toFixed(1)}万`;
        } else if (scaleDenom >= 1000) {
          this.mapScaleText = `1:${(scaleDenom / 1000).toFixed(1)}千`;
        } else {
          this.mapScaleText = `1:${scaleDenom}`;
        }
      } else {
        this.mapScaleText = '-';
      }
    },
    mapZoomIn() {
      if (this.map) this.map.zoomIn();
    },
    mapZoomOut() {
      if (this.map) this.map.zoomOut();
    },
    mapLocate() {
      if (!this.map) return;
      if (!navigator.geolocation) {
        this.$message.warning('浏览器不支持定位');
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          this.map.setView([lat, lng], 14);
        },
        () => this.$message.warning('定位失败'),
        { enableHighAccuracy: true }
      );
    },
    mapHome() {
      if (this.map) this.map.setView([35.8617, 104.1954], 4);
    },
    mapToggleFullscreen() {
      if (!this.map) return;
      const container = this.$refs.mapEl && this.$refs.mapEl.closest('.map-card');
      if (!container) return;
      if (this.isMapFullscreen) {
        (document.exitFullscreen || document.webkitExitFullscreen)?.();
      } else {
        (container.requestFullscreen || container.webkitRequestFullscreen)?.call(container);
      }
      this.$nextTick(() => this.map.invalidateSize());
    },
    handleMapFullscreenChange() {
      this.isMapFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
    },
    async ensureCampusPointsForCurrentView({ force = false } = {}) {
      if (this.toolActiveTab === 'marking' || this.showAllCampusMarkers) {
        await this.loadCampusPoints({ forceAll: true, force });
        return;
      }
      const student = this.getSelectedStudentOption();
      if (student && student.provinceCode) {
        await this.loadCampusPoints({ provinceCode: student.provinceCode, force });
      }
    },
    async loadCampusPoints({ provinceCode = '', forceAll = false, force = false } = {}) {
      const normalizedProvinceCode = provinceCode != null ? String(provinceCode).trim() : '';
      if (!force) {
        if (forceAll && this.allCampusPointsLoaded) {
          this.refreshCampusMarkers();
          return;
        }
        if (!forceAll && normalizedProvinceCode && (this.allCampusPointsLoaded || this.campusPointsProvinceCode === normalizedProvinceCode)) {
          this.refreshCampusMarkers();
          return;
        }
      }
      try {
        const params = forceAll || !normalizedProvinceCode ? {} : { provinceCode: normalizedProvinceCode };
        const res = await api.post('/org/campus-points', params);
        if (res.data && res.data.success) {
          this.campusPoints = res.data.data || [];
          this.allCampusPointsLoaded = forceAll || !normalizedProvinceCode;
          this.campusPointsProvinceCode = this.allCampusPointsLoaded ? '' : normalizedProvinceCode;
          this.tryApplyInitialMarkingOrgDefault();
          this.handleMarkingQuery();
          this.refreshCampusMarkers();
        } else {
          this.$message.error((res.data && res.data.message) || '加载校区点位失败');
        }
      } catch (e) {
        this.$message.error('加载校区点位失败');
      }
    },
    tryApplyInitialMarkingOrgDefault() {
      if (this.markingOrgDefaultApplied) return false;
      const options = this.orgOptions || [];
      if (!options.length) return false;
      const current = String(this.markingForm.orgId || '');
      let changed = false;
      if (!current) {
        this.markingForm.orgId = options[0].value;
        this.markingForm.cityLocationCode = '';
        this.markingForm.campusValue = '';
        changed = true;
      }
      this.markingOrgDefaultApplied = true;
      return changed;
    },
    ensureMarkingTableScrollListener() {
      this.$nextTick(() => {
        const tableRef = this.$refs.markingTableRef;
        if (tableRef && typeof tableRef.doLayout === 'function') tableRef.doLayout();
      });
    },
    handleShowAllCampusMarkersChange() {
      if (this.campusMarkerDebounceTimer) {
        clearTimeout(this.campusMarkerDebounceTimer);
        this.campusMarkerDebounceTimer = null;
      }
      this.campusMarkerDebounceTimer = setTimeout(() => {
        this.campusMarkerDebounceTimer = null;
        this.ensureCampusPointsForCurrentView();
      }, 5000);
    },
    getSelectedStudentOption() {
      if (!this.selectedStudentId) return null;
      return this.selectedStudentOption && String(this.selectedStudentOption.id) === String(this.selectedStudentId)
        ? this.selectedStudentOption
        : null;
    },
    normalizeRegionName(name) {
      return String(name || '')
        .replace(/\s+/g, '')
        .replace(/(壮族自治区|回族自治区|维吾尔自治区|自治区|特别行政区|省|市)$/g, '');
    },
    getCampusProvinceName(campus) {
      const locationCode = campus && campus.locationCode != null ? String(campus.locationCode).trim() : '';
      const cascader = this.$refs.markingRegionCascader;
      if (locationCode && cascader && typeof cascader.getFullPath === 'function') {
        const path = cascader.getFullPath(locationCode) || [];
        const province = path[0] && path[0].name ? String(path[0].name).trim() : '';
        if (province) return province;
      }
      const regionName = this.resolveCampusRegionDisplay(campus);
      const firstPart = String(regionName || '').split('-')[0].trim();
      return firstPart && firstPart !== '-' ? firstPart : '';
    },
    isCampusInStudentProvince(campus, student) {
      if (!campus || !student) return false;
      const campusLocationCode = campus.locationCode != null ? String(campus.locationCode).trim() : '';
      const studentProvinceCode = student.provinceCode != null ? String(student.provinceCode).trim() : '';
      if (campusLocationCode && studentProvinceCode) {
        const parentMap = this.buildRegionParentMap();
        if (Object.keys(parentMap).length && this.isInRegionTree(campusLocationCode, studentProvinceCode, parentMap)) return true;
        if (campusLocationCode === studentProvinceCode || campusLocationCode.slice(0, 2) === studentProvinceCode.slice(0, 2)) return true;
      }
      const studentProvinceName = this.normalizeRegionName(student.provinceName);
      const campusProvinceName = this.normalizeRegionName(this.getCampusProvinceName(campus));
      return !!studentProvinceName && !!campusProvinceName && studentProvinceName === campusProvinceName;
    },
    getVisibleCampusMarkerPoints() {
      if (this.showAllCampusMarkers) return this.campusPoints || [];
      const student = this.getSelectedStudentOption();
      if (!student) return [];
      return (this.campusPoints || []).filter(item => this.isCampusInStudentProvince(item, student));
    },
    updateMarkingTableVisibleData() {
      const all = Array.isArray(this.markingTableAllData) ? this.markingTableAllData : [];
      const size = Math.max(1, Number(this.markingTablePageSize) || 10);
      const maxPage = Math.max(1, Math.ceil(all.length / size));
      if (this.markingTablePage > maxPage) this.markingTablePage = maxPage;
      if (this.markingTablePage < 1) this.markingTablePage = 1;
      const start = (this.markingTablePage - 1) * size;
      this.markingTableData = all.slice(start, start + size);
    },
    resetMarkingTableLazyData(list) {
      this.markingTableAllData = Array.isArray(list) ? list : [];
      this.markingTablePage = 1;
      this.updateMarkingTableVisibleData();
      this.$nextTick(() => {
        const tableRef = this.$refs.markingTableRef;
        const bodyWrapper = tableRef && tableRef.bodyWrapper ? tableRef.bodyWrapper : null;
        if (bodyWrapper) bodyWrapper.scrollTop = 0;
        this.ensureMarkingTableScrollListener();
      });
    },
    handleMarkingPageSizeChange(size) {
      this.markingTablePageSize = Number(size) || 10;
      this.markingTablePage = 1;
      this.updateMarkingTableVisibleData();
      this.$nextTick(() => {
        const tableRef = this.$refs.markingTableRef;
        const bodyWrapper = tableRef && tableRef.bodyWrapper ? tableRef.bodyWrapper : null;
        if (bodyWrapper) bodyWrapper.scrollTop = 0;
        this.ensureMarkingTableScrollListener();
      });
    },
    handleMarkingPageChange(page) {
      this.markingTablePage = Number(page) || 1;
      this.updateMarkingTableVisibleData();
      this.$nextTick(() => {
        const tableRef = this.$refs.markingTableRef;
        const bodyWrapper = tableRef && tableRef.bodyWrapper ? tableRef.bodyWrapper : null;
        if (bodyWrapper) bodyWrapper.scrollTop = 0;
        this.ensureMarkingTableScrollListener();
      });
    },
    markingTableIndexMethod(index) {
      const size = Math.max(1, Number(this.markingTablePageSize) || 10);
      return (this.markingTablePage - 1) * size + index + 1;
    },
    refreshCampusMarkers() {
      this.campusLayer.clearLayers();
      let first = null;
      (this.getVisibleCampusMarkerPoints() || []).forEach(item => {
        if (item.latitude == null || item.longitude == null || Number.isNaN(Number(item.latitude)) || Number.isNaN(Number(item.longitude))) return;
        const lat = Number(item.latitude);
        const lng = Number(item.longitude);
        if (!first) first = [lat, lng];
        const markerIcon = this.createCampusStatusIcon(item.statusCode);
        const marker = L.marker([lat, lng], { icon: markerIcon }).bindPopup(
          this.buildCampusPopupHtml(item),
          { className: this.getCampusPopupClassName(item) }
        );
        marker.addTo(this.campusLayer);
      });
      if (first) this.map.setView(first, 11);
    },
    createCampusStatusIcon(statusCode) {
      const isEnabled = statusCode === 'ENABLED';
      return L.icon({
        iconUrl: isEnabled ? this.enabledCampusMarkerIconUrl : this.disabledCampusMarkerIconUrl,
        iconSize: [36, 54],
        iconAnchor: [18, 53],
        popupAnchor: [0, -48]
      });
    },
    buildCampusPopupHtml(row) {
      const orgName = row && row.orgName ? row.orgName : '-';
      const regionName = this.resolveCampusRegionDisplay(row);
      const campusName = row && row.campusName ? row.campusName : '-';
      const isEnabled = row && row.statusCode === 'ENABLED';
      const statusText = isEnabled ? '启用' : '禁用';
      const orgId = row && row.orgId != null ? String(row.orgId) : '';
      const cityKey = row && row.cityKey ? String(row.cityKey) : '';
      const campusIndex = row && row.campusIndex != null ? String(row.campusIndex) : '';
      const deleteIcon = this.mapMarkingTabRestricted
        ? ''
        : `
          <i
            class="el-icon-delete-location campus-popup-delete"
            title="删除地图标记"
            data-org-id="${orgId}"
            data-city-key="${cityKey}"
            data-campus-index="${campusIndex}"
          ></i>`;
      return `
        <div class="campus-popup-card">
          <div class="campus-popup-title">${orgName}</div>
          <div class="campus-popup-line">${regionName} / ${campusName}</div>
          <div class="campus-popup-status-line">
            <span class="campus-popup-status-label">状态</span>
            <span class="campus-popup-status-badge">${statusText}</span>
          </div>
          ${deleteIcon}
        </div>
      `;
    },
    resolveCampusRegionDisplay(row) {
      const locationCode = row && row.locationCode != null ? String(row.locationCode).trim() : '';
      const cascader = this.$refs.markingRegionCascader;
      if (locationCode && cascader && typeof cascader.getFullPathNames === 'function') {
        const fullPath = String(cascader.getFullPathNames(locationCode) || '').trim();
        if (fullPath) return fullPath;
      }
      const districtName = row && row.districtName != null ? String(row.districtName).trim() : '';
      if (districtName) return districtName;
      const cityKey = row && row.cityKey != null ? String(row.cityKey).trim() : '';
      return cityKey || '-';
    },
    getCampusPopupClassName(row) {
      return row && row.statusCode === 'ENABLED'
        ? 'campus-popup campus-popup-enabled'
        : 'campus-popup campus-popup-disabled';
    },
    attachCampusBubbleDeleteHandlers(rootEl) {
      if (!rootEl) return;
      const deleteEl = rootEl.querySelector('.campus-popup-delete');
      if (deleteEl) {
        deleteEl.onclick = event => {
          event.preventDefault();
          event.stopPropagation();
          const orgId = deleteEl.getAttribute('data-org-id');
          const cityKey = deleteEl.getAttribute('data-city-key');
          const campusIndex = deleteEl.getAttribute('data-campus-index');
          this.openDeleteCampusPopconfirm({ orgId, cityKey, campusIndex }, event);
        };
      }
    },
    handleMapPopupOpen(e) {
      const popupEl = e && e.popup && e.popup.getElement ? e.popup.getElement() : null;
      this.attachCampusBubbleDeleteHandlers(popupEl);
    },
    handleMapTooltipOpen(e) {
      const tooltipEl = e && e.tooltip && e.tooltip.getElement ? e.tooltip.getElement() : null;
      this.attachCampusBubbleDeleteHandlers(tooltipEl);
    },
    handleMapPopupClose() {
      this.hideDeletePopconfirm();
    },
    handleMapTooltipClose() {
      this.hideDeletePopconfirm();
    },
    openDeleteCampusPopconfirm(payload, event) {
      if (this.mapMarkingTabRestricted) return;
      const { orgId, cityKey, campusIndex } = payload || {};
      if (orgId == null || cityKey == null || campusIndex == null) return;
      const clientX = event && event.clientX ? event.clientX : 0;
      const clientY = event && event.clientY ? event.clientY : 0;
      this.deletePopconfirm.payload = { orgId, cityKey, campusIndex };
      this.deletePopconfirm.left = clientX + 2;
      this.deletePopconfirm.top = clientY + 2;
      this.deletePopconfirm.key += 1;
      this.$nextTick(() => {
        if (this.$refs.mapDeletePopRef && typeof this.$refs.mapDeletePopRef.click === 'function') {
          this.$refs.mapDeletePopRef.click();
        }
      });
    },
    hideDeletePopconfirm() {
      if (this.$refs.mapDeletePopconfirmRef) {
        this.$refs.mapDeletePopconfirmRef.showPopper = false;
      }
      this.deletePopconfirm.payload = null;
    },
    confirmDeleteCampusMarker() {
      const payload = this.deletePopconfirm.payload;
      this.hideDeletePopconfirm();
      if (!payload) return;
      this.removeCampusMarker(payload.orgId, payload.cityKey, payload.campusIndex);
    },
    findCampusByKeys(orgId, cityKey, campusIndex) {
      return (this.campusPoints || []).find(item =>
        String(item.orgId) === String(orgId) &&
        String(item.cityKey) === String(cityKey) &&
        String(item.campusIndex) === String(campusIndex)
      );
    },
    clearRecommendOverlays() {
      this.recommendMarkers.forEach(m => this.map.removeLayer(m));
      this.recommendLines.forEach(line => this.map.removeLayer(line));
      this.recommendCarTracks.forEach(ct => ct && ct.destroy && ct.destroy());
      this.recommendMarkers = [];
      this.recommendLines = [];
      this.recommendAnimatedLines = [];
      this.recommendCarTracks = [];
      this.stopRecommendLineAnimation();
    },
    stopRecommendFlow() {
      if (this.recommendCountdownTimer) {
        clearInterval(this.recommendCountdownTimer);
        this.recommendCountdownTimer = null;
      }
      this.recommendCountdown = 0;
      if (this.studentMarker) {
        this.map.removeLayer(this.studentMarker);
        this.studentMarker = null;
      }
      this.clearRecommendOverlays();
      this.recommendList = [];
    },
    async recommendByLocationDesc() {
      const text = (this.studentLocationDesc || '').replace(/\s+/g, ' ').trim();
      if (!text) {
        this.$message.warning('请先输入位置描述');
        return;
      }
      this.locationDescQueryLoading = true;
      this.smartAnalyzeResultLng = '';
      this.smartAnalyzeResultLat = '';
      try {
        const res = await api.post(
          '/map/geocode',
          { text },
          {
            // 仅对地理编码接口放宽超时时间到 5 分钟，避免被前端 axios 默认 30 秒取消
            timeout: 300000
          }
        );
        if (!(res.data && res.data.success && res.data.data)) {
          this.$message.error((res.data && res.data.message) || '位置解析失败');
          return;
        }
        const { longitude, latitude, displayName } = res.data.data;
        if (Number.isNaN(Number(longitude)) || Number.isNaN(Number(latitude))) {
          this.$message.error('位置解析失败：坐标无效');
          return;
        }
        this.studentCoordType = 'WGS84';
        this.studentLng = String(longitude);
        this.studentLat = String(latitude);
        this.smartAnalyzeResultLng = Number(longitude).toFixed(6);
        this.smartAnalyzeResultLat = Number(latitude).toFixed(6);
        this.pendingPoint = { lng: Number(longitude), lat: Number(latitude) };
        this.$message.success(`已定位：${displayName || '位置解析成功'}`);
        this.recommendNearestCampuses();
      } catch (e) {
        this.$message.error('位置解析失败，请输入更完整地址');
      } finally {
        this.locationDescQueryLoading = false;
      }
    },
    startRecommendLineAnimation() {
      this.stopRecommendLineAnimation();
      if (!this.recommendAnimatedLines.length) return;
      this.recommendLineDashOffset = 0;
      this.recommendLineAnimTimer = setInterval(() => {
        this.recommendLineDashOffset -= 1;
        this.recommendAnimatedLines.forEach(line => {
          line.setStyle({ dashOffset: `${this.recommendLineDashOffset}px` });
        });
      }, 80);
    },
    stopRecommendLineAnimation() {
      if (this.recommendLineAnimTimer) {
        clearInterval(this.recommendLineAnimTimer);
        this.recommendLineAnimTimer = null;
      }
    },
    getRecommendLineColor(index, total) {
      const palette = ['#67c23a', '#ffb020', '#ff8f00', '#ff4d4f', '#8b3a2b'];
      if (!total || total <= 1) return palette[0];
      const t = Math.max(0, Math.min(1, index / (total - 1)));
      const scaled = t * (palette.length - 1);
      const fromIdx = Math.floor(scaled);
      const toIdx = Math.min(palette.length - 1, fromIdx + 1);
      const localT = scaled - fromIdx;
      if (fromIdx === toIdx) return palette[fromIdx];
      return this.interpolateHexColor(palette[fromIdx], palette[toIdx], localT);
    },
    interpolateHexColor(fromHex, toHex, t) {
      const from = fromHex.replace('#', '');
      const to = toHex.replace('#', '');
      const fr = parseInt(from.substring(0, 2), 16);
      const fg = parseInt(from.substring(2, 4), 16);
      const fb = parseInt(from.substring(4, 6), 16);
      const tr = parseInt(to.substring(0, 2), 16);
      const tg = parseInt(to.substring(2, 4), 16);
      const tb = parseInt(to.substring(4, 6), 16);
      const r = Math.round(fr + (tr - fr) * t);
      const g = Math.round(fg + (tg - fg) * t);
      const b = Math.round(fb + (tb - fb) * t);
      return `rgb(${r}, ${g}, ${b})`;
    },
    toRgba(color, alpha) {
      const str = String(color);
      if (str.startsWith('rgba(')) {
        const values = str.replace('rgba(', '').replace(')', '').split(',').map(v => Number(String(v).trim()));
        return `rgba(${values[0]}, ${values[1]}, ${values[2]}, ${alpha})`;
      }
      if (str.startsWith('rgb(')) {
        const values = str.replace('rgb(', '').replace(')', '').split(',').map(v => Number(v.trim()));
        return `rgba(${values[0]}, ${values[1]}, ${values[2]}, ${alpha})`;
      }
      const hex = str.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    async removeCampusMarker(orgId, cityKey, campusIndex) {
      if (this.mapMarkingTabRestricted) return;
      if (this.deleteCampusMarkerLoading) return;
      this.deleteCampusMarkerLoading = true;
      try {
        const res = await api.post(`/org/${orgId}/campus-location-remove`, {
          cityKey,
          campusIndex: Number(campusIndex)
        });
        if (res.data && res.data.success) {
          const row = this.findCampusByKeys(orgId, cityKey, campusIndex);
          if (row) {
            row.longitude = null;
            row.latitude = null;
            row.longitudeTencent = null;
            row.latitudeTencent = null;
          }
          this.refreshCampusMarkers();
          this.handleMarkingQuery();
          this.clearRecommendOverlays();
          this.recommendList = [];
          this.map.closePopup();
          this.$message.success('已删除校区地图标记');
        } else {
          this.$message.error((res.data && res.data.message) || '删除校区地图标记失败');
        }
      } catch (e) {
        this.$message.error('删除校区地图标记失败');
      } finally {
        this.deleteCampusMarkerLoading = false;
      }
    },
    /**
     * 右侧地图预标点（校区标注 / 推荐-地图标点共用）。
     * @param {number} lat WGS84
     * @param {number} lng WGS84
     * @param {{ syncRecommend?: boolean }} opts 仅在「学生位置推荐-地图标点」时同步学生坐标并触发推荐倒计时
     */
    placeOrMovePendingMarker(lat, lng, opts = {}) {
      const syncRecommend = !!opts.syncRecommend;
      if (!this.map) return;
      this.pendingPoint = { lng, lat };
      if (syncRecommend) {
        this.fillStudentCoordsFromWgsPoint(lng, lat);
        this.startRecommendCountdown();
      }
      if (this.pendingMarker) this.map.removeLayer(this.pendingMarker);
      const pendingIcon = L.icon({
        iconUrl: this.premarkCampusMarkerIconUrl,
        iconSize: [36, 54],
        iconAnchor: [18, 53],
        popupAnchor: [0, -48]
      });
      this.pendingMarker = L.marker([lat, lng], { draggable: true, icon: pendingIcon }).addTo(this.map);
      this.pendingMarker.on('dragend', evt => {
        const pos = evt.target.getLatLng();
        this.pendingPoint = { lng: pos.lng, lat: pos.lat };
        if (syncRecommend) {
          this.fillStudentCoordsFromWgsPoint(pos.lng, pos.lat);
          this.startRecommendCountdown();
        }
      });
    },
    /** 清除校区标注流程中的预标点（不删已保存的校区图层） */
    clearMarkingPendingPreview() {
      if (this.pendingMarker && this.map) {
        this.map.removeLayer(this.pendingMarker);
        this.pendingMarker = null;
      }
      this.pendingPoint = null;
    },
    handleMapClick(e) {
      const canMarkCampus = !this.mapMarkingTabRestricted && this.toolActiveTab === 'marking';
      const canMapPointRecommend = this.toolActiveTab === 'recommend' && this.recommendInputTab === 'mapPoint';
      if (!canMarkCampus && !canMapPointRecommend) return;
      const { lat, lng } = e.latlng;
      this.placeOrMovePendingMarker(lat, lng, { syncRecommend: canMapPointRecommend });
    },
    /** 校区标注：选择校区后按行政区+详细地址地理编码并自动标点 */
    async handleCampusIndexChange(val) {
      if (this.mapMarkingTabRestricted) return;
      if (this.toolActiveTab !== 'marking') return;
      const v = val != null && val !== '' ? String(val) : '';
      if (!v) {
        this.clearMarkingPendingPreview();
        return;
      }
      if (!this.map) return;
      await this.$nextTick();
      const row = this.findSelectedCampus();
      if (!row) return;
      const district = String(row.districtName || '').trim();
      const addr = String(row.address || '').trim();
      const fullText = [district, addr].filter(Boolean).join('');
      if (!fullText) {
        this.$message.warning('请先在机构管理中完善该校区的「所属行政区」与「校区详细地址」，以便自动解析位置');
        this.clearMarkingPendingPreview();
        return;
      }
      this.markingGeocodeLoading = true;
      try {
        const res = await api.post(
          '/map/geocode',
          { text: fullText },
          { timeout: 300000 }
        );
        if (!(res.data && res.data.success && res.data.data)) {
          this.$message.error((res.data && res.data.message) || '地址解析失败，请检查行政区与详细地址');
          this.clearMarkingPendingPreview();
          return;
        }
        const { longitude, latitude, displayName } = res.data.data;
        if (Number.isNaN(Number(longitude)) || Number.isNaN(Number(latitude))) {
          this.$message.error('地址解析失败：坐标无效');
          this.clearMarkingPendingPreview();
          return;
        }
        const lng = Number(longitude);
        const lat = Number(latitude);
        this.placeOrMovePendingMarker(lat, lng, { syncRecommend: false });
        this.map.setView([lat, lng], 16);
        this.$message.success(`已根据地址自动标点：${displayName || fullText}`);
      } catch (e) {
        this.$message.error('地址解析失败，请稍后重试或检查网络');
        this.clearMarkingPendingPreview();
      } finally {
        this.markingGeocodeLoading = false;
      }
    },
    startRecommendCountdown() {
      if (this.toolActiveTab !== 'recommend') return;
      if (this.recommendCountdownTimer) {
        clearInterval(this.recommendCountdownTimer);
        this.recommendCountdownTimer = null;
      }
      this.recommendCountdown = 5;
      this.recommendCountdownTimer = setInterval(() => {
        this.recommendCountdown -= 1;
        if (this.recommendCountdown <= 0) {
          clearInterval(this.recommendCountdownTimer);
          this.recommendCountdownTimer = null;
          this.recommendCountdown = 0;
          this.recommendNearestCampuses();
        }
      }, 1000);
    },
    buildCampusValue(campus) {
      if (!campus) return '';
      return `${String(campus.cityKey || '')}::${String(campus.campusIndex)}`;
    },
    parseCampusValue(v) {
      const raw = String(v || '');
      const sep = raw.indexOf('::');
      if (sep < 0) return { cityKey: '', campusIndex: NaN };
      const cityKey = raw.slice(0, sep);
      const campusIndex = Number(raw.slice(sep + 2));
      return { cityKey, campusIndex };
    },
    buildRegionParentMap() {
      const comp = this.$refs.markingRegionCascader;
      if (!comp || typeof comp.getRegionData !== 'function') return {};
      const rows = comp.getRegionData() || [];
      const map = {};
      rows.forEach(item => {
        const c = item && item.code != null ? String(item.code).trim() : '';
        const p = item && (item.parent_code || item.parentCode) != null
          ? String(item.parent_code || item.parentCode).trim()
          : '';
        if (c && p) map[c] = p;
      });
      return map;
    },
    isInRegionTree(locationCode, selectedCode, parentMap) {
      const cur = String(locationCode || '').trim();
      const target = String(selectedCode || '').trim();
      if (!cur || !target) return false;
      if (cur === target) return true;
      let walk = cur;
      for (let i = 0; i < 12; i += 1) {
        const p = parentMap[walk];
        if (!p) return false;
        if (p === target) return true;
        walk = p;
      }
      return false;
    },
    isCampusInSelectedRegion(campus, selectedCode) {
      const selected = String(selectedCode || '').trim();
      if (!selected) return false;
      const locationCode = campus && campus.locationCode != null ? String(campus.locationCode).trim() : '';
      if (!locationCode) return false;
      return this.isInRegionTree(locationCode, selected, this.buildRegionParentMap());
    },
    handleOrgChange() {
      this.clearMarkingPendingPreview();
      this.markingForm.cityLocationCode = '';
      this.markingForm.campusValue = '';
      this.$refs.markingFormRef && this.$refs.markingFormRef.clearValidate(['cityLocationCode', 'campusValue']);
    },
    handleCityLocationChange() {
      this.clearMarkingPendingPreview();
      this.markingForm.campusValue = '';
      this.$refs.markingFormRef && this.$refs.markingFormRef.clearValidate(['campusValue']);
    },
    handleMarkingQuery() {
      const { orgId, cityLocationCode, campusValue } = this.markingForm;
      const selectedCampus = campusValue ? this.findSelectedCampus() : null;
      const sortedList = (this.campusPoints || []).filter(item => {
        if (orgId !== '' && orgId != null && String(item.orgId) !== String(orgId)) return false;
        if (cityLocationCode && !this.isCampusInSelectedRegion(item, cityLocationCode)) return false;
        if (selectedCampus) {
          if (String(item.cityKey) !== String(selectedCampus.cityKey)) return false;
          if (Number(item.campusIndex) !== Number(selectedCampus.campusIndex)) return false;
        }
        return true;
      }).map((item, idx) => ({ ...item, _originIndex: idx }))
        .sort((a, b) => {
          const orderA = this.getMarkingTableOrder(a);
          const orderB = this.getMarkingTableOrder(b);
          if (orderA !== orderB) return orderA - orderB;
          return a._originIndex - b._originIndex;
        })
        .map(({ _originIndex, ...rest }) => rest);
      this.resetMarkingTableLazyData(sortedList);
    },
    getMarkingTableOrder(row) {
      const isMarked = this.isCampusMarked(row);
      const isEnabled = row && row.statusCode === 'ENABLED';
      if (isMarked && isEnabled) return 1;
      if (isMarked && !isEnabled) return 2;
      if (!isMarked && isEnabled) return 3;
      return 4;
    },
    handleMarkingResetQuery() {
      this.clearMarkingPendingPreview();
      this.markingForm.orgId = '';
      this.markingForm.cityLocationCode = '';
      this.markingForm.campusValue = '';
      this.$refs.markingFormRef && this.$refs.markingFormRef.clearValidate(['orgId', 'cityLocationCode', 'campusValue']);
      this.handleMarkingQuery();
    },
    handleMarkingRowClick(row) {
      if (!row || row.latitude == null || row.longitude == null || Number.isNaN(Number(row.latitude)) || Number.isNaN(Number(row.longitude))) {
        this.$message.warning('当前校区还未进行地图标记！');
        return;
      }
      const lat = Number(row.latitude);
      const lng = Number(row.longitude);
      this.map.setView([lat, lng], 16);
      this.map.openPopup(
        this.buildCampusPopupHtml(row),
        [lat, lng],
        { className: this.getCampusPopupClassName(row) }
      );
    },
    isCampusMarked(row) {
      return !!(row && row.latitude != null && row.longitude != null &&
        !Number.isNaN(Number(row.latitude)) && !Number.isNaN(Number(row.longitude)));
    },
    getMarkingTableRowClassName({ row }) {
      return this.isCampusMarked(row) ? '' : 'marking-row-unmarked';
    },
    handleMarkingCellMouseEnter(row, column, cell) {
      const rowEl = cell && cell.closest ? cell.closest('tr') : null;
      if (!rowEl) return;
      const tipText = this.isCampusMarked(row)
        ? '点击可在右侧地图上定位该校区位置！'
        : '当前校区还未进行地图标记！';
      const cells = rowEl.querySelectorAll('td');
      cells.forEach(td => td.setAttribute('title', tipText));
    },
    handleMarkingCellMouseLeave(row, column, cell) {
      const rowEl = cell && cell.closest ? cell.closest('tr') : null;
      if (!rowEl) return;
      const cells = rowEl.querySelectorAll('td');
      cells.forEach(td => td.removeAttribute('title'));
    },
    validateMarkingForm() {
      return new Promise(resolve => {
        if (!this.$refs.markingFormRef) {
          resolve(true);
          return;
        }
        this.$refs.markingFormRef.validate(valid => resolve(valid));
      });
    },
    findSelectedCampus() {
      const parsed = this.parseCampusValue(this.markingForm.campusValue);
      if (!parsed.cityKey || Number.isNaN(parsed.campusIndex)) return null;
      return (this.campusPoints || []).find(item =>
        String(item.orgId) === String(this.markingForm.orgId) &&
        String(item.cityKey) === String(parsed.cityKey) &&
        Number(item.campusIndex) === Number(parsed.campusIndex)
      );
    },
    async centerSelectedCampus() {
      const valid = await this.validateMarkingForm();
      if (!valid) return;
      const row = this.findSelectedCampus();
      if (!row || row.latitude == null || row.longitude == null) {
        this.$message.warning('当前校区尚未标注坐标');
        return;
      }
      this.map.setView([Number(row.latitude), Number(row.longitude)], 16);
    },
    fillStudentCoordsFromWgsPoint(wgsLng, wgsLat) {
      let lng = wgsLng;
      let lat = wgsLat;
      if (this.studentCoordType === 'GCJ02') {
        const gcj = wgs84ToGcj02(lng, lat);
        lng = gcj[0];
        lat = gcj[1];
      }
      this.studentLng = Number(lng).toFixed(6);
      this.studentLat = Number(lat).toFixed(6);
    },
    fillStudentCoordsFromMapPoint() {
      if (!this.pendingPoint) {
        this.$message.warning('请先在地图上点击一个位置');
        return;
      }
      this.fillStudentCoordsFromWgsPoint(this.pendingPoint.lng, this.pendingPoint.lat);
      this.$message.success('已填充学生经纬度');
    },
    async saveCampusPoint() {
      if (this.mapMarkingTabRestricted) return;
      const valid = await this.validateMarkingForm();
      if (!valid) return;
      const row = this.findSelectedCampus();
      if (!row) {
        this.$message.warning('请选择有效校区');
        return;
      }
      if (!this.pendingPoint) {
        this.$message.warning('请先在地图上点击标点');
        return;
      }
      this.saveCampusPointLoading = true;
      try {
        const res = await api.post(`/org/${this.markingForm.orgId}/campus-location`, {
          cityKey: row.cityKey,
          campusIndex: Number(row.campusIndex),
          longitude: String(this.pendingPoint.lng),
          latitude: String(this.pendingPoint.lat)
        });
        if (res.data && res.data.success) {
          row.longitude = this.pendingPoint.lng;
          row.latitude = this.pendingPoint.lat;
          const d = res.data.data || {};
          if (d.longitudeTencent != null && d.latitudeTencent != null) {
            row.longitudeTencent = Number(d.longitudeTencent);
            row.latitudeTencent = Number(d.latitudeTencent);
          }
          this.refreshCampusMarkers();
          this.clearMarkingPendingPreview();
          this.markingForm.campusValue = '';
          this.$refs.markingFormRef && this.$refs.markingFormRef.clearValidate(['campusValue']);
          this.handleMarkingQuery();
          this.$message.success('校区坐标已保存');
        } else {
          this.$message.error((res.data && res.data.message) || '保存失败');
        }
      } catch (e) {
        this.$message.error('保存失败');
      } finally {
        this.saveCampusPointLoading = false;
      }
    },
    async recommendNearestCampuses() {
      if (this.toolActiveTab !== 'recommend') return;
      if (this.recommendCountdownTimer) {
        clearInterval(this.recommendCountdownTimer);
        this.recommendCountdownTimer = null;
        this.recommendCountdown = 0;
      }
      const inputLng = Number(this.studentLng);
      const inputLat = Number(this.studentLat);
      if (Number.isNaN(inputLng) || Number.isNaN(inputLat)) {
        this.$message.warning('请输入合法的学生经纬度');
        return;
      }
      let studentWgsLng = inputLng;
      let studentWgsLat = inputLat;
      if (this.studentCoordType === 'GCJ02') {
        const wgs = gcj02ToWgs84(inputLng, inputLat);
        studentWgsLng = wgs[0];
        studentWgsLat = wgs[1];
      }
      const candidates = (this.campusPoints || []).filter(item =>
        item.statusCode === 'ENABLED' &&
        item.latitude != null &&
        item.longitude != null &&
        !Number.isNaN(Number(item.latitude)) &&
        !Number.isNaN(Number(item.longitude))
      ).map(item => ({
        ...item,
        linearDistanceKm: haversineKm(studentWgsLng, studentWgsLat, Number(item.longitude), Number(item.latitude)),
        // 表格展示值改为驾车里程和预计耗时，初始为空，待驾车接口返回后更新
        distanceKm: null,
        durationSeconds: null
      })).sort((a, b) => a.linearDistanceKm - b.linearDistanceKm).slice(0, 5);
      this.recommendList = candidates;
      await this.showStudentAndRecommendedMarkers(studentWgsLat, studentWgsLng, candidates);
    },
    async fetchDriveRouteData(origLng, origLat, destLng, destLat) {
      try {
        const res = await api.post(
          '/map/drive-route',
          {
            origLng: String(origLng),
            origLat: String(origLat),
            destLng: String(destLng),
            destLat: String(destLat)
          },
          { timeout: 30000 }
        );
        if (!(res && res.data && res.data.success && res.data.data)) {
          return null;
        }
        const payload = res.data.data;
        const rawPoints = Array.isArray(payload.points) ? payload.points : [];
        const points = rawPoints
          .map(item => [Number(item[0]), Number(item[1])])
          .filter(item =>
            !Number.isNaN(item[0]) &&
            !Number.isNaN(item[1]) &&
            item[0] >= -90 && item[0] <= 90 &&
            item[1] >= -180 && item[1] <= 180
          );
        const distanceKm = Number(payload.distanceKm);
        const durationSeconds = payload.durationSeconds != null ? Number(payload.durationSeconds) : null;
        const center = payload.center;
        const scale = payload.scale;
        const out = {
          points: points.length >= 2 ? points : null,
          distanceKm: Number.isNaN(distanceKm) ? null : distanceKm,
          durationSeconds: durationSeconds != null && !Number.isNaN(durationSeconds) ? durationSeconds : null,
          center: Array.isArray(center) && center.length >= 2 ? [Number(center[0]), Number(center[1])] : null,
          scale: scale != null && !Number.isNaN(Number(scale)) ? Number(scale) : null
        };
        if (!out.points && out.distanceKm == null && out.durationSeconds == null) {
          return null;
        }
        return out;
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn('[ElectronicMap] drive-route 请求失败', e && e.message ? e.message : e);
        }
        return null;
      }
    },
    async showStudentAndRecommendedMarkers(studentLat, studentLng, rows) {
      if (this.studentMarker) this.map.removeLayer(this.studentMarker);
      this.clearRecommendOverlays();
      const studentPersonIcon = L.divIcon({
        className: 'student-person-marker-wrap',
        html: `<img class="student-person-marker-img" src="${this.studentMarkerPersonImageUrl}" alt="学生位置" />`,
        iconSize: [52, 86],
        iconAnchor: [26, 80],
        popupAnchor: [0, -76]
      });
      this.studentMarker = L.marker([studentLat, studentLng], { icon: studentPersonIcon })
        .addTo(this.map)
        .bindPopup('学生位置（已转换为WGS84）');
      let driveRouteMissingCount = 0;
      let emSettings;
      try {
        emSettings = getElectronicMapSettings();
      } catch (e) {
        emSettings = {};
      }
      const drawTasks = rows.map(async (row, idx) => {
        try {
        const lineColor = (emSettings.routeColor && String(emSettings.routeColor).trim()) || this.getRecommendLineColor(idx, rows.length);
        const routeWidth = Math.max(1, Math.min(20, Number(emSettings.routeWidth) || 6));
        const markerIcon = this.createCampusStatusIcon(row.statusCode);
        const targetLat = Number(row.latitude);
        const targetLng = Number(row.longitude);
        const routeData = await this.fetchDriveRouteData(studentLng, studentLat, targetLng, targetLat);
        if (routeData) {
          if (routeData.distanceKm != null && !Number.isNaN(Number(routeData.distanceKm))) {
            row.distanceKm = Number(routeData.distanceKm);
          }
          if (routeData.durationSeconds != null && !Number.isNaN(Number(routeData.durationSeconds))) {
            row.durationSeconds = Number(routeData.durationSeconds);
          }
          if (routeData.center && Array.isArray(routeData.center) && routeData.center.length >= 2) {
            row.routeCenter = routeData.center;
          }
          if (routeData.scale != null && !Number.isNaN(Number(routeData.scale))) {
            row.routeScale = Number(routeData.scale);
          }
        }
        const distanceText = row.distanceKm != null && !Number.isNaN(Number(row.distanceKm))
          ? Number(row.distanceKm).toFixed(2) + ' km'
          : '-';
        const routeBubbleHtml = `${this.buildCampusPopupHtml(row)}<div class="campus-popup-recommend">驾车距离：${distanceText} · 预计耗时：${this.formatDurationSeconds(row.durationSeconds)}</div>`;
        const routeBubbleClass = this.getCampusPopupClassName(row);
        // 推荐路线校区：不默认展开泡泡，点击标记打开 Popup（与校区标注一致）
        const m = L.marker([targetLat, targetLng], {
          icon: markerIcon,
          zIndexOffset: 50
        })
          .bindPopup(routeBubbleHtml, {
            className: `${routeBubbleClass} campus-recommend-route-bubble`
          })
          .addTo(this.map);
        this.recommendMarkers.push(m);

        // 驾车路线：使用车辆轨迹跟踪动画（等效天地图 CarTrack）
        const linePoints = routeData && routeData.points && routeData.points.length >= 2
          ? routeData.points
          : null;
        if (!linePoints) {
          driveRouteMissingCount += 1;
          return;
        }

        const routeBaseAlpha = Math.max(0, Math.min(1, Number(emSettings.routeBaseAlpha) ?? 0.24));
        let baseColor;
        try {
          baseColor = this.toRgba(lineColor, routeBaseAlpha);
        } catch (_) {
          baseColor = `rgba(255, 69, 0, ${routeBaseAlpha})`;
        }
        const lineBase = L.polyline(linePoints, {
          color: baseColor,
          weight: routeWidth,
          opacity: 1
        }).addTo(this.map);
        this.recommendLines.push(lineBase);

        // 路线文案必须绑在「静态」折线上；绑在动画层 notPassedLine 上会因 setLatLngs 每帧更新导致 permanent Tooltip 不显示（本地/云上均可能）
        const lineTooltipText = `${row.orgName || '-'} / ${row.campusName || '-'} · ${distanceText} · ${this.formatDurationSeconds(row.durationSeconds)}`;
        lineBase.bindTooltip(lineTooltipText, {
          permanent: true,
          direction: 'center',
          className: 'recommend-line-tooltip',
          opacity: 1,
          interactive: false
        });
        try {
          lineBase.openTooltip();
        } catch (e) {
          /* ignore */
        }

        const carTrack = createCarTrack(this.map, linePoints, {
          interval: emSettings.interval ?? 25,
          speed: emSettings.carSpeed ?? 500,
          passedColor: emSettings.passedRouteColor || lineColor,
          notPassedColor: emSettings.notPassedRouteColor || lineColor,
          lineWeight: emSettings.trajectoryLineWeight ?? routeWidth,
          lineOpacity: emSettings.trajectoryLineOpacity ?? 0.9,
          carIconSize: emSettings.carIconSize ?? 28,
          carEmoji: emSettings.carEmoji || '🚗',
          showHistoryTrack: emSettings.showHistoryTrack === '1',
          showTooltip: false,
          tooltipText: '',
          loop: emSettings.loopPlay !== false
        });
        if (carTrack) {
          this.recommendCarTracks.push(carTrack);
        }
        } catch (err) {
          console.warn('绘制驾车路线失败:', row.campusName, err);
        }
      });
      await Promise.all(drawTasks);
      if (driveRouteMissingCount > 0) {
        this.$message.warning(`有 ${driveRouteMissingCount} 条推荐未获取到驾车路线，请稍后重试`);
      }
      this.recommendCarTracks.forEach(ct => ct && ct.start && ct.start());
      if (rows.length) {
        const FIXED_ZOOM = 13;
        this.map.setView([studentLat, studentLng], FIXED_ZOOM);
      } else {
        this.map.setView([studentLat, studentLng], 13);
      }
      // 地图 setView 后部分环境下 Tooltip 需再次打开
      this.$nextTick(() => {
        (this.recommendLines || []).forEach((layer) => {
          try {
            if (layer && typeof layer.openTooltip === 'function') layer.openTooltip();
          } catch (e) {
            /* ignore */
          }
        });
      });
    },
    handleRecommendRowClick(row) {
      if (!row || row.latitude == null || row.longitude == null || Number.isNaN(Number(row.latitude)) || Number.isNaN(Number(row.longitude))) {
        this.$message.warning('该校区坐标无效，无法在地图定位');
        return;
      }
      const lat = Number(row.latitude);
      const lng = Number(row.longitude);
      this.map.setView([lat, lng], 16);
      const recommendExtra = (row.distanceKm != null || row.durationSeconds != null)
        ? `<div class="campus-popup-recommend">驾车距离：${row.distanceKm != null ? Number(row.distanceKm).toFixed(2) + ' km' : '-'} · 预计耗时：${this.formatDurationSeconds(row.durationSeconds)}</div>`
        : '';
      this.map.openPopup(
        `${this.buildCampusPopupHtml(row)}${recommendExtra}`,
        [lat, lng],
        { className: this.getCampusPopupClassName(row) }
      );
    }
  }
};
</script>

<style scoped>
.electronic-map-page {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 12px;
}

.tool-card {
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.resize-handle {
  width: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: col-resize;
  background: transparent;
  transition: background 0.15s;
}

.resize-handle:hover,
.resize-handle:active {
  background: rgba(64, 158, 255, 0.1);
}

.resize-handle-bar {
  width: 4px;
  height: 40px;
  border-radius: 2px;
  background: #dcdfe6;
}

.resize-handle:hover .resize-handle-bar,
.resize-handle:active .resize-handle-bar {
  background: #409eff;
}

.recommend-duration-cell {
  white-space: nowrap;
}

.tool-card >>> .el-card__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.map-card {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.map-card >>> .el-card__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 12px;
}

.map-canvas {
  flex: 1;
  min-height: 620px;
  border: 1px solid #ebeef5;
  border-radius: 6px 6px 0 0;
}

.map-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-top: none;
  border-radius: 0 0 6px 6px;
}

.map-toolbar-tools {
  display: flex;
  align-items: center;
  gap: 8px;
}

.map-toolbar-tools .el-button {
  margin: 0;
}

.map-campus-all-switch {
  display: inline-flex;
  align-items: center;
}

.map-toolbar-info {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 12px;
  color: #606266;
}

.map-toolbar-scale,
.map-toolbar-zoom {
  white-space: nowrap;
}

.map-canvas >>> .student-person-marker-wrap {
  background: transparent;
  border: none;
}

.map-canvas >>> .student-person-marker-img {
  width: 52px;
  height: 86px;
  object-fit: contain;
  filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.28));
  pointer-events: none;
}

.map-canvas >>> .car-track-marker-wrap {
  background: transparent;
  border: none;
}

.map-canvas >>> .car-track-marker {
  font-size: 24px;
  line-height: 1;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.35));
  pointer-events: none;
}

.map-canvas >>> .recommend-line-tooltip {
  background: rgba(255, 255, 255, 0.97);
  border: 1px solid #b3d8ff;
  color: #2f7fd6;
  border-radius: 999px;
  box-shadow: 0 2px 10px rgba(64, 158, 255, 0.22);
  font-size: 12px;
  line-height: 1.2;
  padding: 4px 10px;
  white-space: nowrap;
  animation: recommendLineTooltipPulse 1.8s ease-in-out infinite;
}

.map-canvas >>> .recommend-line-tooltip:before {
  display: none;
}

@keyframes recommendLineTooltipPulse {
  0% {
    box-shadow: 0 2px 10px rgba(64, 158, 255, 0.18);
  }
  50% {
    box-shadow: 0 2px 12px rgba(64, 158, 255, 0.3);
  }
  100% {
    box-shadow: 0 2px 10px rgba(64, 158, 255, 0.18);
  }
}

/* 校区泡泡：白底透明度 0.2 + 背后高斯模糊（毛玻璃，与推荐路线 Tooltip 一致） */
.map-canvas >>> .leaflet-popup.campus-popup .leaflet-popup-content-wrapper {
  border-radius: 12px;
  padding: 0;
  border: 1px solid rgba(228, 231, 237, 0.85);
  background: rgba(255, 255, 255, 0.2) !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

/* 推荐路线校区：permanent Tooltip 与 Popup 视觉统一 */
.map-canvas >>> .leaflet-tooltip.campus-popup {
  padding: 0 !important;
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

.map-canvas >>> .leaflet-tooltip.campus-popup::before {
  display: none;
}

.map-canvas >>> .leaflet-tooltip.campus-popup .leaflet-tooltip-content {
  margin: 0;
  padding: 0;
  border-radius: 12px;
  border: 1px solid rgba(228, 231, 237, 0.85);
  background: rgba(255, 255, 255, 0.2) !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.map-canvas >>> .leaflet-popup.campus-popup .leaflet-popup-content {
  margin: 0;
}

.map-canvas >>> .leaflet-popup.campus-popup .leaflet-popup-tip-container {
  margin-top: -1px;
}

.map-canvas >>> .leaflet-popup.campus-popup .leaflet-popup-tip {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.map-canvas >>> .leaflet-popup.campus-popup .campus-popup-card,
.map-canvas >>> .leaflet-tooltip.campus-popup .campus-popup-card {
  position: relative;
  min-width: 210px;
  padding: 14px 16px 34px;
  border-radius: 10px;
  border-left: 4px solid transparent;
  font-size: 13px;
  color: #1f2937;
  background: transparent;
}

.map-canvas >>> .leaflet-popup.campus-popup .campus-popup-title,
.map-canvas >>> .leaflet-tooltip.campus-popup .campus-popup-title {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: 0.2px;
  margin-bottom: 6px;
}

.map-canvas >>> .leaflet-popup.campus-popup .campus-popup-delete,
.map-canvas >>> .leaflet-tooltip.campus-popup .campus-popup-delete {
  position: absolute;
  right: 12px;
  bottom: 10px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #9ca3af;
  background: #f8fafc;
  border: 1px solid #edf2f7;
  cursor: pointer;
  transition: transform 0.18s ease, color 0.18s ease, background-color 0.18s ease, border-color 0.18s ease;
}

.map-canvas >>> .leaflet-popup.campus-popup .campus-popup-delete:hover,
.map-canvas >>> .leaflet-tooltip.campus-popup .campus-popup-delete:hover {
  color: #f56c6c;
  background: #fff4f4;
  border-color: #ffd6d6;
  transform: scale(1.08);
}

.map-canvas >>> .leaflet-popup.campus-popup .campus-popup-line,
.map-canvas >>> .leaflet-tooltip.campus-popup .campus-popup-line {
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 10px;
}

.map-canvas >>> .leaflet-popup.campus-popup .campus-popup-status-line,
.map-canvas >>> .leaflet-tooltip.campus-popup .campus-popup-status-line {
  display: flex;
  align-items: center;
  gap: 10px;
}

.map-canvas >>> .leaflet-popup.campus-popup .campus-popup-status-label,
.map-canvas >>> .leaflet-tooltip.campus-popup .campus-popup-status-label {
  color: #9ca3af;
  font-size: 13px;
}

.map-canvas >>> .leaflet-popup.campus-popup .campus-popup-status-badge,
.map-canvas >>> .leaflet-tooltip.campus-popup .campus-popup-status-badge {
  padding: 3px 10px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 12px;
  line-height: 1.2;
  border: 1px solid transparent;
}

.map-canvas >>> .leaflet-popup.campus-popup .campus-popup-recommend,
.map-canvas >>> .leaflet-tooltip.campus-popup .campus-popup-recommend {
  margin: 0 16px 12px;
  padding-top: 10px;
  border-top: 1px dashed #e5e7eb;
  color: #6b7280;
  font-size: 12px;
}

.map-canvas >>> .leaflet-popup.campus-popup-enabled .campus-popup-card,
.map-canvas >>> .leaflet-tooltip.campus-popup-enabled .campus-popup-card {
  border-left-color: #34c759;
}

.map-canvas >>> .leaflet-popup.campus-popup-enabled .campus-popup-status-badge,
.map-canvas >>> .leaflet-tooltip.campus-popup-enabled .campus-popup-status-badge {
  background: #ecfdf3;
  border-color: #bbf7d0;
  color: #16a34a;
}

.map-canvas >>> .leaflet-popup.campus-popup-disabled .campus-popup-card,
.map-canvas >>> .leaflet-tooltip.campus-popup-disabled .campus-popup-card {
  border-left-color: #f97373;
}

.map-canvas >>> .leaflet-popup.campus-popup-disabled .campus-popup-status-badge,
.map-canvas >>> .leaflet-tooltip.campus-popup-disabled .campus-popup-status-badge {
  background: #fff1f1;
  border-color: #fecaca;
  color: #dc2626;
}

.map-delete-pop-anchor {
  position: fixed;
  z-index: 3000;
  width: 14px;
  height: 14px;
}

.map-delete-pop-ref {
  display: block;
  width: 14px;
  height: 14px;
  opacity: 0;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;
  background: transparent;
  cursor: default;
}

.tool-section {
  margin-bottom: 14px;
}

.tool-tabs {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.tool-tabs >>> .el-tabs__header {
  margin-bottom: 10px;
}

.tool-tabs >>> .el-tabs__content {
  flex: 1;
  min-height: 0;
}

.tool-tabs >>> .el-tab-pane {
  height: 100%;
}

.tool-tabs >>> .el-tabs__nav {
  width: 100%;
  display: flex;
}

.tool-tabs >>> .el-tabs__item {
  flex: 1;
  text-align: center;
  padding: 0 8px;
  font-size: 13px;
}

.tool-tabs >>> .el-tabs__item.is-active {
  background-color: #ecf5ff;
  color: #409eff;
}

.section-title {
  font-size: 14px;
  color: #303133;
  margin-bottom: 8px;
}

.point-text {
  color: #606266;
}

.recommend-section {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.recommend-inner-tabs {
  flex: 0 0 auto;
}

.recommend-inner-tabs >>> .el-tabs__header {
  margin-bottom: 8px;
}

.recommend-mode-pane .el-form {
  margin-bottom: 0;
}

.smart-analyze-coord {
  color: #606266;
  font-size: 12px;
  line-height: 18px;
  margin: -2px 0 8px 100px;
}

.recommend-table-wrap {
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}

.recommend-table-wrap >>> .el-table__body-wrapper,
.recommend-table-wrap >>> .el-table__header-wrapper {
  overflow: hidden !important;
}

.recommend-table {
  margin-top: 8px;
  min-width: 455px;
}

.recommend-min-tag {
  color: #67c23a;
  font-weight: 500;
}

.recommend-badge-blink {
  animation: recommend-badge-blink 1s ease-in-out infinite;
}

@keyframes recommend-badge-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}

.marking-section {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.marking-section .el-form {
  flex: 0 0 auto;
}

.marking-address-tip {
  color: #909399;
  font-size: 12px;
  line-height: 1.55;
  margin: -4px 0 10px 100px;
  padding-right: 8px;
}

.marking-table-wrap {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.marking-table-wrap >>> .el-table__body tr.marking-row-unmarked > td {
  background-color: rgba(255, 183, 77, 0.5) !important;
}

.marking-table-tip {
  color: #909399;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 6px;
}

.marking-table-tip-mark {
  color: #53a8ff;
  margin-right: 4px;
}

.marking-table-stat {
  margin-left: 12px;
}

.marking-table-stat-marked {
  color: #88e822;
}

.marking-table-stat-unmarked {
  color: #f42aa2;
}

.marking-pagination {
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
}

.marking-pagination >>> .el-pagination__total {
  margin-right: 8px;
}

.student-map-tip {
  color: #909399;
  font-size: 13px;
  margin-bottom: 8px;
}

</style>

<!-- 推荐线文案：非 scoped，避免生产构建下 Leaflet 节点未命中 .map-canvas[data-v-xxx] 导致样式/层级异常 -->
<style>
.electronic-map-page .leaflet-tooltip-pane {
  z-index: 800 !important;
}

.leaflet-tooltip.recommend-line-tooltip {
  background: rgba(255, 255, 255, 0.97) !important;
  border: 1px solid #b3d8ff !important;
  color: #2f7fd6 !important;
  border-radius: 999px !important;
  box-shadow: 0 2px 10px rgba(64, 158, 255, 0.22) !important;
  font-size: 12px !important;
  line-height: 1.2 !important;
  padding: 4px 10px !important;
  white-space: nowrap !important;
  animation: recommendLineTooltipPulseGlobal 1.8s ease-in-out infinite;
}

.leaflet-tooltip.recommend-line-tooltip::before {
  display: none !important;
}

@keyframes recommendLineTooltipPulseGlobal {
  0% {
    box-shadow: 0 2px 10px rgba(64, 158, 255, 0.18);
  }
  50% {
    box-shadow: 0 2px 12px rgba(64, 158, 255, 0.3);
  }
  100% {
    box-shadow: 0 2px 10px rgba(64, 158, 255, 0.18);
  }
}
</style>
